import { useEffect, useRef, useState } from 'react';
import { Button, ButtonLinkProps, RegularButtonProps } from '@/components/button';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import styles from './expandable-info.module.css';
import clsx from 'clsx';
import { useTranslation } from '@/contexts/i18n-context';
import { useLocation, useNavigate } from '@/util/react-router-dom';
import { createEditUrl } from '@/util/helpers';
import { useUpdateEncountersStatus } from '@/api/update-encounter-status';
import { useEncounter } from '@/contexts/encounter-context';
import { EncounterStatusIdsType, UpdateEncounterStatusErrorType } from '@/api/api.schemas';
import { Modal, ModalButtonContainer } from '@/components/modal/modal';
import { BrokenLinkSvg } from '@/components/svgs/broken-link';

interface EditModeProps {
  editTo: string;
  editModeParam: string;
}

interface ExpandableInfoProps {
  title: string;
  children: React.ReactNode;
  isOpen?: boolean;
  editProps?: EditModeProps;
  className?: string;
}

export function ExpandableInfo({
  title,
  children,
  isOpen = false,
  editProps,
  className,
}: ExpandableInfoProps) {
  const [isExpanded, setIsExpanded] = useState(isOpen);

  return (
    <div className={clsx(styles.detailsExpanders, className)} data-testid="review-expandable-panel">
      <div className={styles.titleBar}>
        <Button
          variant="none"
          onClick={() => setIsExpanded(!isExpanded)}
          fullWidth
          data-testid="review-section-expand-toggle"
          aria-expanded={isExpanded}
        >
          <div className={styles.expandButton}>
            <MaterialSymbolIcon className={styles.icon} name={isExpanded ? 'remove' : 'add'} />
            <h3 className="h4">{title}</h3>
          </div>
        </Button>

        {editProps && (
          <ReviewEditButton
            to={editProps.editTo}
            editModeParam={editProps.editModeParam}
            setIsExpanded={setIsExpanded}
          />
        )}
      </div>
      <div
        className={clsx(styles.details, 'body-2', isExpanded ? styles.shown : styles.hidden)}
        data-testid="review-expandable-details"
      >
        {children}
      </div>
    </div>
  );
}

interface DataRowProps {
  title: string;
  children: React.ReactNode;
  unformatted?: boolean;
}

export function DataRow({ title, children, unformatted = false }: DataRowProps) {
  return (
    <dl className={clsx(styles.dataRow, { [styles.unformatted]: unformatted })}>
      <dt className={styles.dataTitle}>{title}</dt>
      <dd className={styles.dataValue}>{children}</dd>
    </dl>
  );
}

interface ReviewEditButtonProps {
  to: string;
  editModeParam: string;
  text?: string;
  setIsExpanded?: (isExpanded: boolean) => void;
}

export function ReviewEditButton({
  to,
  editModeParam,
  text,
  setIsExpanded,
}: ReviewEditButtonProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const editMode = (location.state as { editMode?: string | null })?.editMode;
  const isOnFocus = editMode === editModeParam;
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const { invalidateEncounter } = useEncounter();

  const { encounter } = useEncounter();
  const { mutate: updateEncounterStatus, error } = useUpdateEncountersStatus({
    mutation: {
      onSuccess: () => {
        invalidateEncounter();
        navigate(createEditUrl(to, editModeParam));
      },
    },
  });

  useEffect(() => {
    if (isOnFocus) {
      setIsExpanded?.(true);
      buttonRef.current?.focus();
    }
  }, [isOnFocus, setIsExpanded]);

  const buttonProps: Omit<RegularButtonProps, 'children'> | Omit<ButtonLinkProps, 'children'> =
    encounter.statusId >= EncounterStatusIdsType.InWaitingRoom
      ? {
          as: 'button',
          onClick: () =>
            updateEncounterStatus({
              encounterId: encounter.id,
              data: { statusId: EncounterStatusIdsType.PreSubmitReview },
            }),
        }
      : { as: 'a', to: createEditUrl(to, editModeParam) };

  return (
    <>
      <Button ref={buttonRef} variant="link" leftIcon="edit" {...buttonProps}>
        {text ?? t('visit.review.editButton')}
      </Button>
      <EditErrorModal error={error?.type} />
    </>
  );
}

function EditErrorModal({ error }: { error?: UpdateEncounterStatusErrorType }) {
  const { t } = useTranslation();

  const isInvalidTransition = error === UpdateEncounterStatusErrorType.InvalidTransition;

  const errorTranslationBase = isInvalidTransition
    ? 'visit.review.editButtonModal.invalidTransition'
    : 'visit.review.editButtonModal.generic';

  return (
    <Modal isOpen={!!error} hideCloseButton>
      {!isInvalidTransition && <BrokenLinkSvg className={styles.brokenLink} />}
      <h2 className="h3">{t(`${errorTranslationBase}.title`)}</h2>
      <p className={styles.errorDescription}>
        {t(`${errorTranslationBase}.description`, {
          link: (
            <Button key="visit-already-picked-up-link" as="a" to="/support" variant="link">
              {t('visit.review.editButtonModal.contactSupport')}
            </Button>
          ),
        })}
      </p>
      <ModalButtonContainer>
        <Button as="a" to="/dashboard">
          {t('visit.review.editButtonModal.goBackToDashboard')}
        </Button>
      </ModalButtonContainer>
    </Modal>
  );
}
